/**
 * The Yeast Bay — a California strain bank whose cultures White Labs
 * propagates and sells under the WLP4xxx codes. The specs here are read from
 * whitelabs.com, which is where the numbers are published; the strains stay
 * under The Yeast Bay's name because that is whose bank they came from.
 *
 * Seasonal releases carry a `vault` tag, matching the White Labs file.
 * Temperatures are °C as published (PLAN §5 canon), attenuation is apparent
 * attenuation, and `alcoholTolerancePercent` keeps the upper figure of White
 * Labs' band.
 *
 * The two Lactobacillus entries carry no published attenuation or
 * flocculation, so they alone are `representative`.
 */

import type { Yeast } from '../../schema/yeast';

/** The Yeast Bay cultures. @see Yeast */
export const yeastBayYeasts: readonly Yeast[] = [
  {
    id: 'yeast-bay-vermont-ale',
    lab: 'The Yeast Bay',
    productCode: 'WLP4000',
    name: 'Vermont Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 82 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'The original Vermont DIPA isolate: peach, apricot and light citrus on a single Saccharomyces strain, not a blend. The culture that made hazy IPA a style — still the reference when a recipe says "Vermont ale yeast".',
    tags: ['hazy', 'new-england', 'ipa', 'vermont', 'stone-fruit', 'esters'],
    url: 'https://www.whitelabs.com/yeast-single?id=312&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-flanders-specialty',
    lab: 'The Yeast Bay',
    productCode: 'WLP4001',
    name: 'Flanders Specialty Ale',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 78, max: 82 },
    temperatureC: { min: 20, max: 27 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: true,
    description:
      'A Flanders strain for oud bruin and Flanders red, attenuating hard and running warm. It carries STA1, so a beer built with it will keep drying out in the cellar.',
    tags: ['flanders', 'sour', 'oud-bruin', 'sta1', 'warm', 'complex', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=315&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-saison-blend',
    lab: 'The Yeast Bay',
    productCode: 'WLP4007',
    name: 'Saison Blend',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 84 },
    temperatureC: { min: 20, max: 27 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 15,
    sta1: true,
    description:
      'Several saison isolates pitched together so one strain leads and another finishes — pepper, lemon zest and a very dry beer without the classic Dupont parking-brake. Diastatic; do not share lines with beers you need stable.',
    tags: ['saison', 'blend', 'farmhouse', 'phenolic', 'diastatic', 'dry-finish', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=281&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-northeastern-abbey',
    lab: 'The Yeast Bay',
    productCode: 'WLP4015',
    name: 'Northeastern Abbey',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 77, max: 81 },
    temperatureC: { min: 20, max: 24 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'An abbey-style isolate from the northeastern US: dark fruit, a restrained clove phenol and enough alcohol tolerance for dubbel through quadrupel. Start cool and let it rise; it is not a witbier yeast.',
    tags: ['belgian', 'abbey', 'phenolic', 'dubbel', 'tripel', 'esters', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=280&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-wallonian-farmhouse',
    lab: 'The Yeast Bay',
    productCode: 'WLP4020',
    name: 'Wallonian Farmhouse',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 81, max: 100 },
    temperatureC: { min: 22, max: 27 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: true,
    description:
      'A Dupont-region farmhouse isolate: earthy, peppery and citric, with the well-earned habit of slowing down before it actually finishes. Give it warmth and time; it is diastatic, so packaging early is how bottles become bombs.',
    tags: ['saison', 'farmhouse', 'wallonian', 'phenolic', 'diastatic', 'belgian', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=283&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-saison-blend-ii',
    lab: 'The Yeast Bay',
    productCode: 'WLP4021',
    name: 'Saison Blend II',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 85, max: 100 },
    temperatureC: { min: 20, max: 27 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: true,
    description:
      'A second saison blend that attenuates to 100%, which is as dry as a farmhouse beer gets. It carries STA1 and wants a warm, patient fermentation.',
    tags: ['saison', 'blend', 'farmhouse', 'sta1', 'attenuative', 'dry', 'warm', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=282&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-wallonian-farmhouse-ii',
    lab: 'The Yeast Bay',
    productCode: 'WLP4022',
    name: 'Wallonian Farmhouse II Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 80, max: 82 },
    temperatureC: { min: 20, max: 27 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'The second Wallonian farmhouse isolate, spicier and less aggressive than the first. It finishes around 82% without the STA1 the original carries.',
    tags: ['saison', 'wallonian', 'farmhouse', 'spicy', 'dry', 'warm', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=284&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-wallonian-farmhouse-iii',
    lab: 'The Yeast Bay',
    productCode: 'WLP4023',
    name: 'Wallonian Farmhouse III Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 86, max: 94 },
    temperatureC: { min: 20, max: 27 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 15,
    sta1: true,
    description:
      'The third Wallonian isolate and the most attenuative of the three, reaching 94%. It carries STA1, which is where that last stretch comes from.',
    tags: ['saison', 'wallonian', 'farmhouse', 'sta1', 'attenuative', 'dry', 'warm', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=285&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-dry-belgian',
    lab: 'The Yeast Bay',
    productCode: 'WLP4025',
    name: 'Dry Belgian Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 85, max: 100 },
    temperatureC: { min: 20, max: 26 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 15,
    sta1: true,
    description:
      'A Belgian strain selected for dryness, attenuating to 100% while still flocculating well. It carries STA1; treat anything bottled with it accordingly.',
    tags: ['belgian', 'dry', 'attenuative', 'sta1', 'flocculent', 'warm', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=277&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-funktown-pale-ale',
    lab: 'The Yeast Bay',
    productCode: 'WLP4027',
    name: 'Funktown Pale Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 85 },
    temperatureC: { min: 20, max: 23 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 12,
    sta1: true,
    description:
      'Vermont Ale blended with a STA1+ Saccharomyces that adds pineapple and mango on top of the peach-citrus. Fruit-forward and hop-friendly, with low diastatic activity that is still enough to keep drying a beer in package.',
    tags: ['hazy', 'ipa', 'pale-ale', 'tropical', 'blend', 'diastatic', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=308&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-franconian-dark-lager',
    lab: 'The Yeast Bay',
    productCode: 'WLP4030',
    name: 'Franconian Dark Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 74, max: 78 },
    temperatureC: { min: 8, max: 12 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A Franconian lager strain for dark lagers, fermenting cold at 8–12 °C and finishing malty. Slower than the industrial German strains, and richer for it.',
    tags: ['lager', 'franconian', 'dark', 'malty', 'cold', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=268&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-hessian-pils',
    lab: 'The Yeast Bay',
    productCode: 'WLP4035',
    name: 'Hessian Pils Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 73, max: 76 },
    temperatureC: { min: 7, max: 12 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A German pils strain that ferments down to 7 °C and finishes crisp around 76%. Low attenuation for a pilsner strain, which leaves a little body behind.',
    tags: ['lager', 'pilsner', 'german', 'crisp', 'cold', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=269&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-midwestern-ale',
    lab: 'The Yeast Bay',
    productCode: 'WLP4040',
    name: 'Midwestern Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 80 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A heartland American ale isolate: clean and fast at the cool end, a little fruitier if you let it warm. A genuine house-yeast candidate for anything that is not trying to be hazy or Belgian.',
    tags: ['american', 'clean', 'house', 'pale-ale', 'amber', 'versatile', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=311&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-hazy-daze',
    lab: 'The Yeast Bay',
    productCode: 'WLP4042',
    name: 'Hazy Daze',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 79, max: 83 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'Three Saccharomyces strains blended for hop-forward beers: peach, apricot, nectarine and grapefruit esters with enough attenuation that a hazy IPA finishes drinkable. Not diastatic.',
    tags: ['hazy', 'ipa', 'blend', 'stone-fruit', 'citrus', 'esters'],
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-hazy-daze-ii',
    lab: 'The Yeast Bay',
    productCode: 'WLP4044',
    name: 'Hazy Daze II',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 81, max: 85 },
    temperatureC: { min: 19, max: 22 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: null,
    sta1: true,
    description:
      'Hazy Daze plus a STA1+ partner: faster, drier, and louder in mango, guava and grapefruit. The lab calls the diastatic activity low, but it is still STA1 positive — keep it off shared packaging and confirm it has actually finished.',
    tags: ['hazy', 'ipa', 'blend', 'tropical', 'diastatic', 'dry-finish'],
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-sigmunds-voss',
    lab: 'The Yeast Bay',
    productCode: 'WLP4045',
    name: "Sigmund's Voss Kveik",
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 78, max: 83 },
    temperatureC: { min: 21, max: 38 },
    flocculation: 'high',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'The Sigmund Gjernes Voss culture as Yeast Bay isolated it: orange-citrus esters, a hard floc and a two-to-three-day finish when held hot. One of the first kveik packs most North American brewers ever saw.',
    tags: ['kveik', 'voss', 'orange', 'norwegian', 'fast', 'farmhouse', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=274&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-simonaitis',
    lab: 'The Yeast Bay',
    productCode: 'WLP4046',
    name: 'Simonaitis Lithuanian Farmhouse Ale',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 24, max: 35 },
    flocculation: 'high',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'A Lithuanian farmhouse strain that ferments at 24–35 °C and flocculates hard. Closer to a kveik than to a saison in how it behaves.',
    tags: ['kveik', 'lithuanian', 'farmhouse', 'hot', 'flocculent', 'fast', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=275&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-pakruojis',
    lab: 'The Yeast Bay',
    productCode: 'WLP4047',
    name: 'Pakruojis Lithuanian Farmhouse Ale',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 90, max: 100 },
    temperatureC: { min: 24, max: 35 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: true,
    description:
      'The Pakruojis farmhouse strain, attenuating to 100% at farmhouse temperatures. It carries STA1 and flocculates poorly, so plan for a hazy, very dry beer.',
    tags: ['kveik', 'lithuanian', 'farmhouse', 'sta1', 'attenuative', 'hot', 'low-floc', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=276&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-hornindal-kveik',
    lab: 'The Yeast Bay',
    productCode: 'WLP4050',
    name: 'Hornindal Kveik',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 77, max: 81 },
    temperatureC: { min: 27, max: 35 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'Hornindal farmhouse kveik: pineapple, mango and stone fruit rather than Voss orange. Ferment it hot under tropical hops and it will finish a hazy IPA before the weekend is over.',
    tags: ['kveik', 'hornindal', 'tropical', 'norwegian', 'fast', 'ipa', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=271&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-framgarden-kveik',
    lab: 'The Yeast Bay',
    productCode: 'WLP4051',
    name: 'Framgarden Kveik',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 78, max: 82 },
    temperatureC: { min: 27, max: 35 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'A Framgarden farmhouse isolate with a softer citrus-and-honey ester than Voss and a similarly hard floc. A good kveik when you want speed without a loud orange note sitting on top of the hops.',
    tags: ['kveik', 'framgarden', 'citrus', 'norwegian', 'fast', 'farmhouse', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=270&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-lida-kveik',
    lab: 'The Yeast Bay',
    productCode: 'WLP4052',
    name: 'Lida Kveik Ale',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 75, max: 82 },
    temperatureC: { min: 27, max: 32 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'The Lida kveik, working at 27–32 °C and finishing in days. It flocculates poorly for a kveik, so give it a cold rest before packaging.',
    tags: ['kveik', 'lida', 'norwegian', 'hot', 'fast', 'low-floc', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=272&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-midtbust-kveik',
    lab: 'The Yeast Bay',
    productCode: 'WLP4053',
    name: 'Midtbust Kveik Ale',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 76, max: 80 },
    temperatureC: { min: 24, max: 35 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'The Midtbust kveik, across a wide 24–35 °C band with a moderate attenuation. Low flocculation, which is unusual in this family.',
    tags: ['kveik', 'midtbust', 'norwegian', 'hot', 'fast', 'low-floc', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=273&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-forager',
    lab: 'The Yeast Bay',
    productCode: 'WLP4060',
    name: 'Forager Ale',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 85, max: 90 },
    temperatureC: { min: 20, max: 27 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: true,
    description:
      'A wild ale strain that attenuates to 90% with the funk that implies. It carries STA1 and belongs in a beer that will be aged rather than rushed.',
    tags: ['brett', 'wild', 'attenuative', 'sta1', 'funk', 'warm', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=278&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-rhine-kolsch',
    lab: 'The Yeast Bay',
    productCode: 'WLP4061',
    name: 'Rhine Kölsch Ale',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 75, max: 78 },
    temperatureC: { min: 14, max: 20 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A kölsch strain that ferments from 14 °C, cooler than most, and finishes crisp and clean. It attenuates modestly, which keeps a light beer from going thin.',
    tags: ['kolsch', 'german', 'crisp', 'clean', 'hybrid', 'cool', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=313&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-la-fleur',
    lab: 'The Yeast Bay',
    productCode: 'WLP4062',
    name: 'La Fleur Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 23, max: 27 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'A warm-fermenting Belgian strain with a floral character, happiest at 23–27 °C. It sits between a saison and a Belgian pale in profile.',
    tags: ['belgian', 'floral', 'warm', 'esters', 'saison', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=279&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-beersel-brett',
    lab: 'The Yeast Bay',
    productCode: 'WLP4605',
    name: 'Beersel Brettanomyces',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 82, max: 85 },
    temperatureC: { min: 21, max: 27 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: null,
    description:
      'A single Brettanomyces bruxellensis isolated from a lambic made around Beersel, in Flemish Brabant. It gives a balanced profile rather than the extremes at either end of the Brett range.',
    tags: ['brett', 'bruxellensis', 'beersel', 'lambic', 'balanced', 'aging', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=289&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-brussels-brett',
    lab: 'The Yeast Bay',
    productCode: 'WLP4615',
    name: 'Brussels Brettanomyces',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 80, max: 90 },
    temperatureC: { min: 21, max: 27 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 15,
    sta1: null,
    description:
      'A Brussels Brettanomyces isolate attenuating to 90%, for classic Belgian funk in secondary. It works slowly and rewards a long conditioning.',
    tags: ['brett', 'brussels', 'funk', 'attenuative', 'secondary', 'aging', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=290&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-lochristi-brett',
    lab: 'The Yeast Bay',
    productCode: 'WLP4620',
    name: 'Lochristi Brettanomyces',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 80, max: 88 },
    temperatureC: { min: 21, max: 27 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 15,
    sta1: null,
    description:
      'A Brettanomyces bruxellensis from a beer made in Lochristi, East Flanders, and assertively fruity where the Beersel isolate is balanced. Fruit rather than barnyard, at real volume.',
    tags: ['brett', 'bruxellensis', 'lochristi', 'fruity', 'assertive', 'aging', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=291&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-saison-brett-blend-i',
    lab: 'The Yeast Bay',
    productCode: 'WLP4626',
    name: 'Saison/Brettanomyces Blend I',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 80, max: 100 },
    temperatureC: { min: 21, max: 26 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 15,
    sta1: null,
    description:
      'A saison and Brettanomyces blend that finishes at 100%, with farmhouse spice under the funk. Give it months rather than weeks before judging.',
    tags: ['saison', 'brett', 'blend', 'dry', 'farmhouse', 'attenuative', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=301&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-melange',
    lab: 'The Yeast Bay',
    productCode: 'WLP4633',
    name: 'Mélange Blend',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 85, max: 100 },
    temperatureC: { min: 20, max: 26 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 15,
    sta1: null,
    description:
      'The Mélange sour blend: Saccharomyces, Brettanomyces and souring bacteria together, attenuating to 100%. A year in a carboy is the shortest useful timeline.',
    tags: ['sour', 'blend', 'brett', 'lacto', 'pedio', 'aging', 'attenuative', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=300&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-saison-brett-blend-ii',
    lab: 'The Yeast Bay',
    productCode: 'WLP4636',
    name: 'Saison/Brettanomyces Blend II',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 85, max: 90 },
    temperatureC: { min: 22, max: 27 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 15,
    sta1: null,
    description:
      'A second saison and Brett blend, running slightly warmer and finishing a shade less dry than the first. More funk, a little less attenuation.',
    tags: ['saison', 'brett', 'blend', 'farmhouse', 'funk', 'warm', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=302&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-amalgamation-i',
    lab: 'The Yeast Bay',
    productCode: 'WLP4637',
    name: 'Amalgamation I Brettanomyces Blend',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 85, max: 95 },
    temperatureC: { min: 21, max: 27 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: null,
    description:
      'The original Amalgamation blend: several Brettanomyces strains together, attenuating to 95%. Intended for primary fermentation of a Brett beer rather than as a secondary pitch.',
    tags: ['brett', 'blend', 'funk', 'attenuative', 'low-floc', 'aging', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=286&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-brett-tyb207',
    lab: 'The Yeast Bay',
    productCode: 'WLP4638',
    name: 'Brettanomyces bruxellensis TYB207',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 82, max: 88 },
    temperatureC: { min: 22, max: 28 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 15,
    sta1: null,
    description:
      'A single Brettanomyces bruxellensis isolate, attenuating to 88% at 22–28 °C. Cleaner and more predictable than a blend, and easier to build a recipe around.',
    tags: ['brett', 'bruxellensis', 'isolate', 'funk', 'attenuative', 'aging', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=292&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-brett-tyb412',
    lab: 'The Yeast Bay',
    productCode: 'WLP4639',
    name: 'Brettanomyces bruxellensis TYB412',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 80, max: 82 },
    temperatureC: { min: 22, max: 28 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 15,
    sta1: null,
    description:
      'A Brettanomyces bruxellensis isolate that stops around 82%, gentler than TYB207. A good first single-strain Brett.',
    tags: ['brett', 'bruxellensis', 'isolate', 'funk', 'moderate', 'aging', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=293&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-brett-tyb417',
    lab: 'The Yeast Bay',
    productCode: 'WLP4640',
    name: 'Brettanomyces bruxellensis TYB417',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 80, max: 82 },
    temperatureC: { min: 22, max: 28 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 15,
    sta1: null,
    description:
      'Another bruxellensis isolate in the same 80–82% band, differing in character rather than attenuation. Worth splitting a batch to compare against TYB412.',
    tags: ['brett', 'bruxellensis', 'isolate', 'funk', 'moderate', 'aging', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=294&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-amalgamation-ii',
    lab: 'The Yeast Bay',
    productCode: 'WLP4641',
    name: 'Amalgamation II Brettanomyces Blend',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 82, max: 86 },
    temperatureC: { min: 21, max: 27 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: null,
    description:
      'The second Amalgamation blend, attenuating to 86% rather than 95%. Less aggressive than the original, and easier to keep on the right side of dry.',
    tags: ['brett', 'blend', 'funk', 'low-floc', 'aging', 'moderate', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=287&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-oud-vat-brett',
    lab: 'The Yeast Bay',
    productCode: 'WLP4642',
    name: 'Oud Vat Brett',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 85, max: 90 },
    temperatureC: { min: 21, max: 27 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: null,
    description:
      'A Brettanomyces isolated from an old fermentation vat, attenuating to 90%. The character is barrel and cellar rather than tropical fruit.',
    tags: ['brett', 'oud-bruin', 'barrel', 'funk', 'attenuative', 'aging', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=297&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-amalgamation-v',
    lab: 'The Yeast Bay',
    productCode: 'WLP4643',
    name: 'Amalgamation V Brettanomyces Blend',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 85, max: 90 },
    temperatureC: { min: 21, max: 27 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 15,
    sta1: null,
    description:
      'The fifth Amalgamation blend, back at 90% attenuation with a different mix of isolates. The Amalgamation series is meant to be tried against itself.',
    tags: ['brett', 'blend', 'funk', 'attenuative', 'aging', 'complex', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=288&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-transatlantic-berliner',
    lab: 'The Yeast Bay',
    productCode: 'WLP4645',
    name: 'Transatlantic Berliner Blend',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 85, max: 100 },
    temperatureC: { min: 19, max: 24 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'A Berliner weisse blend that takes the beer to 100% while souring it. Faster than a traditional mixed fermentation, and tart rather than funky.',
    tags: ['berliner-weisse', 'sour', 'blend', 'lacto', 'tart', 'attenuative', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=303&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-metschnikowia',
    lab: 'The Yeast Bay',
    productCode: 'WLP4650',
    name: 'Metschnikowia reukaufii',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 20, max: 25 },
    temperatureC: { min: 16, max: 32 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A nectar yeast that attenuates barely at all, around 20–25%, and is used for aroma rather than fermentation. Pitch it alongside a primary strain, not instead of one.',
    tags: ['wild', 'non-saccharomyces', 'low-attenuation', 'floral', 'experimental', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=307&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-von-degenberg',
    lab: 'The Yeast Bay',
    productCode: 'WLP4651',
    name: 'Von Degenberg Hefe Blend',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 20, max: 22 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'A hefeweizen blend with the usual banana and clove balance and a modest 76% attenuation. It leaves the body a wheat beer wants.',
    tags: ['wheat', 'hefeweizen', 'blend', 'banana', 'clove', 'low-floc', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=339&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-dark-belgian-cask',
    lab: 'The Yeast Bay',
    productCode: 'WLP4653',
    name: 'Dark Belgian Cask Blend',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 80, max: 85 },
    temperatureC: { min: 20, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: null,
    description:
      'A blend for dark Belgian beers destined for cask or barrel, attenuating to 85%. It develops slowly, which is the point of putting it in wood.',
    tags: ['belgian', 'dark', 'cask', 'blend', 'brett', 'aging', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=298&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-brett-tyb422',
    lab: 'The Yeast Bay',
    productCode: 'WLP4655',
    name: 'Brettanomyces bruxellensis TYB422',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 80, max: 84 },
    temperatureC: { min: 21, max: 27 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: null,
    description:
      'A bruxellensis isolate in the 80–84% band, low-flocculating and slow. One of the more classic barnyard profiles in the series.',
    tags: ['brett', 'bruxellensis', 'isolate', 'funk', 'attenuative', 'low-floc', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=295&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-brett-tyb424',
    lab: 'The Yeast Bay',
    productCode: 'WLP4656',
    name: 'Brettanomyces bruxellensis TYB424',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 82, max: 86 },
    temperatureC: { min: 21, max: 27 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: null,
    description:
      'A bruxellensis isolate attenuating to 86%, at the drier end of the single-strain Bretts. Expect months before the character settles.',
    tags: ['brett', 'bruxellensis', 'isolate', 'funk', 'attenuative', 'low-floc', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=296&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-berkeley-hills-sour',
    lab: 'The Yeast Bay',
    productCode: 'WLP4665',
    name: 'Berkeley Hills Sour',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 62, max: 75 },
    temperatureC: { min: 18, max: 20 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A sour culture that stops between 62% and 75%, leaving real body under the acidity. It flocculates well and ferments cool, which is unusual for a sour blend.',
    tags: ['sour', 'low-attenuation', 'flocculent', 'tart', 'blend', 'cool', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=306&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-farmhouse-sour',
    lab: 'The Yeast Bay',
    productCode: 'WLP4675',
    name: 'Farmhouse Sour Ale Blend',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 80, max: 100 },
    temperatureC: { min: 21, max: 26 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 12,
    sta1: true,
    description:
      'A farmhouse sour blend that attenuates to 100% while souring, at saison temperatures. It carries STA1, so treat packaged beer with respect.',
    tags: ['farmhouse', 'sour', 'blend', 'saison', 'sta1', 'attenuative', 'warm', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=299&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'yeast-bay-lacto-brevis-tyb282',
    lab: 'The Yeast Bay',
    productCode: 'WLP4681',
    name: 'Lactobacillus brevis TYB282',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 30, max: 40 },
    temperatureC: { min: 20, max: 32 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A Lactobacillus brevis isolate for souring at 20–32 °C. White Labs prints no attenuation or flocculation for it, so those figures here are class-typical.',
    tags: ['lacto', 'brevis', 'sour', 'kettle-sour', 'tart', 'warm', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=305&type=YEAST',
    provenance: 'representative',
  },
  {
    id: 'yeast-bay-lactobacillus-blend',
    lab: 'The Yeast Bay',
    productCode: 'WLP4682',
    name: 'Lactobacillus Blend',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 30, max: 40 },
    temperatureC: { min: 20, max: 32 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A blend of Lactobacillus strains for a broader souring profile than a single isolate gives. As with the single strain, the attenuation and flocculation here are class-typical.',
    tags: ['lacto', 'blend', 'sour', 'kettle-sour', 'tart', 'warm', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=304&type=YEAST',
    provenance: 'representative',
  },
  {
    id: 'yeast-bay-house-sour',
    lab: 'The Yeast Bay',
    productCode: 'WLP4684',
    name: 'The Bay House Sour Blend',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 85, max: 100 },
    temperatureC: { min: 20, max: 26 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 15,
    sta1: true,
    description:
      "The Yeast Bay's house sour blend, attenuating to 100% with Brettanomyces character over the acidity. It carries STA1 and needs a year to resolve.",
    tags: ['sour', 'blend', 'house', 'brett', 'sta1', 'attenuative', 'aging', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=338&type=YEAST',
    provenance: 'spec-sheet',
  },
];
